import React from 'react';
import { Grid, Box, Typography } from '@mui/material';
import CustomAccordion from '../ui/CustomAccordion';

interface TrackingParameters {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  sid?: string;
  psecId?: string;
  gclid?: string;
  fbclid?: string;
  atid?: string;
}

interface TrackingParametersSectionProps {
  trackingParams: TrackingParameters;
  expandedSection: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null;
  setExpandedSection: (
    section: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null
  ) => void;
  fieldConfig?: Array<{ key: string; label: string }>;
  headerText?: string;
}

const InfoField: React.FC<{ label: string; value: string | undefined }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
    <Typography
      variant="body2"
      fontWeight={600}
      sx={{
        fontSize: '13px',
        color: (theme: any) => theme.palette.neutral7?.main,
        minWidth: '110px',
      }}
    >
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ fontSize: '13px', wordBreak: 'break-all' }}>
      {value || '-'}
    </Typography>
  </Box>
);

const TrackingParametersSection: React.FC<TrackingParametersSectionProps> = ({
  trackingParams,
  expandedSection,
  setExpandedSection,
  fieldConfig,
  headerText = 'Tracking Parameters',
}) => {
  // Default fields if not provided by Sitecore
  const defaultFields = [
    { key: 'utmSource', label: 'UTM Source' },
    { key: 'utmMedium', label: 'UTM Medium' },
    { key: 'utmCampaign', label: 'UTM Campaign' },
    { key: 'utmTerm', label: 'UTM Term' },
    { key: 'utmContent', label: 'UTM Content' },
    { key: 'sid', label: 'sID' },
    { key: 'psecId', label: 'psecID' },
    { key: 'gclid', label: 'GCLID' },
    { key: 'fbclid', label: 'FBCLID' },
    { key: 'atid', label: 'ATID' },
  ];

  const fields = fieldConfig && fieldConfig.length > 0 ? fieldConfig : defaultFields;

  const hasData =
    trackingParams &&
    Object.keys(trackingParams).length > 0 &&
    fields.some((field) => {
      const value = (trackingParams as any)[field.key];
      return value !== null && value !== undefined && value !== '';
    });

  if (!hasData) return null;

  return (
    <CustomAccordion
      title={headerText}
      expanded={expandedSection === 'tracking'}
      onChange={() => setExpandedSection(expandedSection === 'tracking' ? null : 'tracking')}
      sx={{ mt: 1.25 }}
      detailsSx={{ p: 1.25 }}
    >
      <Grid container spacing={1.25}>
        {fields.map((field) => (
          <Grid item xs={12} sm={6} md={3} key={field.key}>
            <InfoField label={field.label} value={(trackingParams as any)[field.key]} />
          </Grid>
        ))}
      </Grid>
    </CustomAccordion>
  );
};

export default TrackingParametersSection;
